import { useCallback, useEffect, useState } from 'react';

import {
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
  type FirestoreError,
  getDoc,
  getDocFromServer,
  onSnapshot,
} from 'firebase/firestore';

import errorHandler from '@server/logger/errorHandler';

type UseFirestoreDocumentFetchOptions = {
  realtime?: boolean;
  useCache?: boolean;
};

const useFirestoreDocumentFetch = <T extends DocumentData>(
  docRef: DocumentReference<T> | null,
  options: UseFirestoreDocumentFetchOptions = {},
) => {
  const { realtime = true, useCache = true } = options;

  const [data, setData] = useState<(T & { id: string }) | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const handleSnapshot = useCallback((snapshot: DocumentSnapshot<T>) => {
    if (!snapshot.exists()) {
      setData(null);
      setLoading(false);
      return;
    }
    const docData = snapshot.data();
    setData({ ...docData, id: snapshot.id });
    setLoading(false);
  }, []);

  const handleError = useCallback((error: FirestoreError) => {
    errorHandler({ error });
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!docRef) {
      setLoading(false);
      return;
    }

    const fetchDocument = async () => {
      try {
        const snapshot = await (!useCache ? getDocFromServer(docRef) : getDoc(docRef));
        handleSnapshot(snapshot);
      } catch (error) {
        handleError(error as FirestoreError);
      }
    };

    fetchDocument();

    if (realtime) {
      const unsubscribe = onSnapshot(docRef, handleSnapshot, handleError);
      return unsubscribe;
    }
  }, [docRef, realtime, handleSnapshot, handleError, useCache]);

  return { data, loading };
};

export default useFirestoreDocumentFetch;
